'use client'

import Link from 'next/link'
import { useState } from 'react'

import { useKeenSlider } from 'keen-slider/react'
import { KeenSliderArrows } from '@/components/keen-slider/KeenSliderPlugins'
import 'keen-slider/keen-slider.min.css'

import AppImage from '@/components/AppImage'

const keyMap = {
  'fintech': 'fintech',
  'sustainable-finance': 'sustainable-finance',
  'banking': 'banking',
  'corporate-services': 'corporate-services',
  'investment-management': 'investment-management',
  'master': 'master',
  'funds': 'funds',
  'private-wealth': 'private-wealth',
}

export default function SectorKeenSlider({
  posts,
  title,
  text,
}: {
  posts: any[]
  title?: string
  text?: string
}) {
  const [isDisabled, setIsDisabled] = useState(false)

  const [sliderRef, instanceRef] = useKeenSlider({
    initial: 0,
    slides: {
      perView: 1.25,
      spacing: 16,
    },
    breakpoints: {
      '(min-width: 540px)': {
        slides: {
          perView: 2.25,
          spacing: 16,
        },
      },
      '(min-width: 700px)': {
        slides: {
          perView: 3.25,
          spacing: 16,
        },
      },
      '(min-width: 1000px)': {
        slides: {
          perView: 4,
          spacing: 16,
        },
      },
      '(min-width: 1400px)': {
        slides: {
          perView: 5,
          spacing: 24,
        },
      },
    },
    created(s) {
      setIsDisabled(s.options.disabled)
    },
    detailsChanged(s) {
      setIsDisabled(s.options.disabled)
    },
  })

  if (!posts || posts.length === 0) {
    return null
  }

  return (
    <>
      <div ref={sliderRef} className="keen-slider">
        {posts.map((post) => {
          const {
            id,
            title: { rendered: postTitle },
            slug,
            link,
            acf: { grid_image: { url: gridImageUrl, alt: gridImageAlt = '' } } = {},
          } = post || {}

          const gradientName = keyMap[slug] ? keyMap[slug] : 'master'

          const itemClass = `
              gradient--${gradientName}
              gradient-angle-[90deg]
              theme--gradient-light

              group
              w-full
              relative
              flex
              flex-col
              items-center
              justify-center
              rounded-md
              py-xxl
              px-l
              text-center
              text-title
              overflow-hidden
            `

          return (
            <div className="keen-slider__slide flex" key={id}>
              <Link href={link} className={itemClass}>
                <div
                  className={`sector-inner gradient--${gradientName} absolute inset-0 gradient-angle-[180deg] theme--gradient-light`}></div>

                <div className="relative z-10 flex h-full w-full transform flex-col items-center gap-l transition-transform duration-300 group-hover:translate-y-[-4px]">
                  {gridImageUrl ? (
                    <AppImage
                      path={gridImageUrl}
                      alt={gridImageAlt}
                      className="w-[min(120px,75%)]"
                      width="120"
                      height="120"
                      transforms={{
                        width: '120',
                        height: '120',
                        dpr: '2',
                      }}
                      sizes="(min-width: 840px) 120px, (min-width: 700px) calc(17.5vw - 24px), (min-width: 340px) 120px, calc(575vw - 1720px)"
                      srcSet={{
                        120: { width: 120, height: 120 },
                        240: { width: 240, height: 240 },
                        480: { width: 480, height: 480 },
                      }}
                    />
                  ) : null}

                  <h4 className="text-balance">{postTitle}</h4>
                </div>
              </Link>
            </div>
          )
        })}
      </div>

      <div className="[@media(max-width:1000px)]:pr-[var(--container-padding)]">
        <KeenSliderArrows sliderInstance={instanceRef} disabled={isDisabled} />
      </div>
    </>
  )
}
